/**
 * Owner: Masa Bayu
 * File: pdf-raster.ts
 * Date:Time: 2026-09-30 15:38
 * Description: Uncompressed PDF sample plus soft mask into one IR image block.
 */

import {
  createQXDocumentAssetRef,
  createQXDocumentBlock,
  createQXDocumentIR,
  createQXDocumentLayout,
  type QXDocumentIR,
  type QXDocumentRaster,
} from '@qx/qx-document-ir';

const latin1 = new TextDecoder('latin1');

function bytesOf(text: string): Uint8Array {
  return Uint8Array.from(text, (char) => char.charCodeAt(0));
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = haystack.length - needle.length;
  for (let index = from; index <= last; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return index;
  }
  return -1;
}

function textOf(file: Uint8Array, start: number, end: number): string {
  return latin1.decode(file.subarray(start, end));
}

function objectText(file: Uint8Array, id: number): string {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  if (marked < 0) throw new Error(`PDF object ${String(id)} is missing`);
  const slice = file.subarray(marked + 1);
  const end = indexOfBytes(slice, bytesOf('endobj'));
  return textOf(slice, 0, end < 0 ? slice.length : end);
}

function readStream(file: Uint8Array, id: number): { readonly dict: string; readonly stream: Uint8Array } {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  if (marked < 0) throw new Error(`PDF object ${String(id)} is missing`);
  const slice = file.subarray(marked + 1);
  const streamAt = indexOfBytes(slice, bytesOf('stream'));
  if (streamAt < 0) throw new Error(`PDF object ${String(id)} has no stream`);
  const dict = textOf(slice, 0, streamAt);
  const length = Number(dict.match(/\/Length\s+(\d+)/)?.[1] ?? 0);
  let dataAt = streamAt + 'stream'.length;
  if (slice[dataAt] === 0x0d) dataAt += 1;
  if (slice[dataAt] === 0x0a) dataAt += 1;
  return { dict, stream: slice.subarray(dataAt, dataAt + length) };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index] ?? 0);
  return btoa(binary);
}

function reference(dict: string, name: string): number {
  return Number(dict.match(new RegExp(`/${name}\\s+(\\d+)\\s+0\\s+R`))?.[1] ?? 0);
}

function integer(dict: string, name: string): number {
  return Number(dict.match(new RegExp(`/${name}\\s+(\\d+)`))?.[1] ?? 0);
}

function matte(dict: string): readonly number[] {
  const raw = dict.match(/\/Matte\s*\[([^\]]+)\]/)?.[1] ?? '';
  return raw.trim().length === 0 ? [] : raw.trim().split(/\s+/).map(Number);
}

function packedLength(width: number, height: number, components: number, bitDepth: number): number {
  return Math.ceil((width * components * bitDepth) / 8) * height;
}

function samplesOf(file: Uint8Array, id: number, components: number): { readonly dict: string; readonly samples: string; readonly width: number; readonly height: number; readonly bitDepth: number } | null {
  const image = readStream(file, id);
  if (/\/Filter\b/.test(image.dict)) return null;
  const width = integer(image.dict, 'Width');
  const height = integer(image.dict, 'Height');
  const bitDepth = integer(image.dict, 'BitsPerComponent');
  if (width <= 0 || height <= 0 || ![1, 2, 4, 8, 16].includes(bitDepth)) return null;
  if (image.stream.length !== packedLength(width, height, components, bitDepth)) return null;
  if (image.stream[0] === 0xff && image.stream[1] === 0xd8) return null;
  return { dict: image.dict, samples: base64(image.stream), width, height, bitDepth };
}

function withMask(file: Uint8Array, color: { readonly dict: string; readonly samples: string; readonly width: number; readonly height: number; readonly bitDepth: number }, colorSpace: string): QXDocumentRaster | null {
  const maskId = reference(color.dict, 'SMask');
  if (!maskId) return null;
  const mask = samplesOf(file, maskId, 1);
  if (!mask || !mask.dict.includes('/ColorSpace /DeviceGray')) return null;
  return {
    samples: color.samples,
    width: color.width,
    height: color.height,
    colorSpace,
    bitDepth: color.bitDepth,
    mask: {
      samples: mask.samples,
      width: mask.width,
      height: mask.height,
      colorSpace: 'DeviceGray',
      bitDepth: mask.bitDepth,
      matte: matte(mask.dict),
    },
  };
}

function indexedSpace(dict: string): string | null {
  const match = dict.match(/\/ColorSpace\s*\[\s*\/Indexed\s+\/DeviceRGB\s+(\d+)\s*<([0-9A-Fa-f]+)>\s*\]/);
  if (!match?.[1] || !match[2]) return null;
  return `/Indexed /DeviceRGB ${match[1]} <${match[2]}>`;
}

export function decodedPdfRaster(file: Uint8Array, id: number): QXDocumentRaster | null {
  const color = samplesOf(file, id, 3);
  if (color?.dict.includes('/ColorSpace /DeviceRGB')) {
    const direct = withMask(file, color, 'DeviceRGB');
    if (direct) return direct;
  }
  const indexed = samplesOf(file, id, 1);
  const space = indexed ? indexedSpace(indexed.dict) : null;
  if (!indexed || !space) return null;
  return withMask(file, indexed, space);
}

function imageNames(file: Uint8Array, page: string): ReadonlyMap<string, number> {
  const resourcesId = reference(page, 'Resources');
  const resources = resourcesId ? objectText(file, resourcesId) : page;
  const xObjectId = reference(resources, 'XObject');
  const source = xObjectId ? objectText(file, xObjectId) : resources;
  const names = new Map<string, number>();
  for (const item of source.matchAll(/\/(Image\d+)\s+(\d+)\s+0\s+R/g)) names.set(item[1] ?? '', Number(item[2]));
  return names;
}

type OpenPage = {
  readonly file: Uint8Array;
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly stream: string;
  readonly names: ReadonlyMap<string, number>;
};

function openPage(bytes: Uint8Array, pageNumber: number): OpenPage {
  if (!Number.isInteger(pageNumber) || pageNumber < 1) throw new Error('pageNumber must be a positive integer');
  const file = Uint8Array.from(bytes);
  const fileText = textOf(file, 0, file.length);
  // Same Pages-dict Kids resolution as pdf-pages.pageKids (Kids may follow /Type /Pages).
  let at = fileText.indexOf('/Type /Pages');
  if (at < 0) at = fileText.indexOf('/Type/Pages');
  const dictStart = at >= 0 ? fileText.lastIndexOf('<<', at) : -1;
  const dictEnd = at >= 0 ? fileText.indexOf('>>', at) : -1;
  const window =
    dictStart >= 0 && dictEnd > dictStart
      ? fileText.slice(dictStart, dictEnd + 2)
      : fileText.slice(Math.max(0, at < 0 ? 0 : at - 240), Math.min(fileText.length, at < 0 ? 0 : at + 240));
  const kids = [...(window.match(/\/Kids\s*\[([^\]]+)\]/)?.[1] ?? '').matchAll(/(\d+)\s+0\s+R/g)].map((item) => Number(item[1]));
  const pageId = kids.length === 1 ? kids[0] ?? 0 : kids[pageNumber - 1] ?? 0;
  if (!pageId) throw new Error('PDF page is missing');
  const page = objectText(file, pageId);
  const box = page.match(/\/MediaBox\s*\[\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*\]/);
  const pageWidth = Number(box?.[1] ?? 0);
  const pageHeight = Number(box?.[2] ?? 0);
  const contentsId = reference(page, 'Contents');
  if (pageWidth <= 0 || pageHeight <= 0 || !contentsId) throw new Error('PDF page is incomplete');
  const content = readStream(file, contentsId);
  if (/\/Filter\b/.test(content.dict)) throw new Error('PDF content stream is not plain text');
  return {
    file,
    pageWidth,
    pageHeight,
    stream: textOf(content.stream, 0, content.stream.length),
    names: imageNames(file, page),
  };
}

/**
 * Reads one uncompressed page and writes each decoded masked sample
 * onto an IR image block.
 */
export function importPdfRaster(bytes: Uint8Array, pageNumber: number): QXDocumentIR {
  const opened = openPage(bytes, pageNumber);
  const file = opened.file;
  const pageHeight = opened.pageHeight;
  const stream = opened.stream;
  const names = opened.names;
  const blocks = [];
  const assets = [];
  for (const item of stream.matchAll(/([-\d.]+)\s+0\s+0\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+cm\s*\/(Image\d+)\s+Do/g)) {
    const name = item[5] ?? '';
    const objectId = names.get(name) ?? 0;
    if (!objectId) continue;
    const raster = decodedPdfRaster(file, objectId);
    if (!raster) continue;
    const identity = `page-${String(pageNumber)}/${name}`;
    const width = Number(item[1]);
    const height = Number(item[2]);
    const bbox = [Number(item[3]), pageHeight - Number(item[4]) - height, width, height] as const;
    blocks.push(
      createQXDocumentBlock({
        blockId: `blk_${identity.replace(/[^A-Za-z0-9]+/g, '_')}`,
        type: 'IMAGE',
        order: blocks.length,
        assetId: identity,
        raster,
        provenance: { sourceType: 'pdf', sourcePage: pageNumber, sourceObjectId: identity, bbox: [...bbox] },
      }),
    );
    assets.push(
      createQXDocumentAssetRef({
        assetId: identity,
        kind: 'image',
        provenance: { sourceType: 'pdf', sourcePage: pageNumber, sourceObjectId: identity },
      }),
    );
  }
  if (blocks.length === 0) throw new Error('PDF page has no decoded raster with a soft mask');
  return createQXDocumentIR({
    irId: `ir_page_${String(pageNumber)}_raster`,
    sourceType: 'pdf',
    sourceHash: 'sha256:qxeditor-poc-raster-adapter-01',
    provenance: { sourceType: 'pdf', sourcePage: pageNumber },
    layout: createQXDocumentLayout({
      pages: [{ pageId: `irpage_${String(pageNumber)}`, pageNumber, width: opened.pageWidth, height: pageHeight }],
    }),
    blocks,
    assets,
  });
}

/**
 * One uncompressed page: substantial JPEG images and decoded mask rasters,
 * in content-stream order.
 */
export function importPdfRasterPage(bytes: Uint8Array, pageNumber: number): QXDocumentIR {
  const opened = openPage(bytes, pageNumber);
  const blocks = [];
  const assets = [];
  for (const item of opened.stream.matchAll(/([-\d.]+)\s+0\s+0\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+cm\s*\/(Image\d+)\s+Do/g)) {
    const name = item[5] ?? '';
    const objectId = opened.names.get(name) ?? 0;
    if (!objectId) continue;
    const image = readStream(opened.file, objectId);
    const width = Number(item[1]);
    const height = Number(item[2]);
    const bbox = [Number(item[3]), opened.pageHeight - Number(item[4]) - height, width, height] as const;
    const jpeg = image.stream[0] === 0xff && image.stream[1] === 0xd8 && image.stream[2] === 0xff;
    if (jpeg && width >= 80 && height >= 80) {
      const assetId = `asset_${String(objectId)}`;
      blocks.push(
        createQXDocumentBlock({
          blockId: `blk_p${String(pageNumber)}_obj_${String(objectId)}`,
          type: 'IMAGE',
          order: blocks.length,
          assetId,
          text: `data:image/jpeg;base64,${base64(image.stream)}`,
          provenance: { sourceType: 'pdf', sourcePage: pageNumber, sourceObjectId: assetId, bbox: [...bbox] },
        }),
      );
      assets.push(createQXDocumentAssetRef({ assetId, kind: 'image', provenance: { sourceType: 'pdf', sourcePage: pageNumber, sourceObjectId: assetId } }));
      continue;
    }
    const raster = decodedPdfRaster(opened.file, objectId);
    if (!raster) continue;
    const identity = `page-${String(pageNumber)}/${name}`;
    blocks.push(
      createQXDocumentBlock({
        blockId: `blk_${identity.replace(/[^A-Za-z0-9]+/g, '_')}`,
        type: 'IMAGE',
        order: blocks.length,
        assetId: identity,
        raster,
        provenance: { sourceType: 'pdf', sourcePage: pageNumber, sourceObjectId: identity, bbox: [...bbox] },
      }),
    );
    assets.push(createQXDocumentAssetRef({ assetId: identity, kind: 'image', provenance: { sourceType: 'pdf', sourcePage: pageNumber, sourceObjectId: identity } }));
  }
  if (!blocks.some((block) => block.assetId === `page-${String(pageNumber)}/Image83`) || !blocks.some((block) => (block.text ?? '').startsWith('data:image/jpeg'))) {
    throw new Error('PDF page is missing the JPEG plate or Image83');
  }
  return createQXDocumentIR({
    irId: `ir_page_${String(pageNumber)}_raster_page`,
    sourceType: 'pdf',
    sourceHash: 'sha256:qxeditor-poc-raster-page-01',
    provenance: { sourceType: 'pdf', sourcePage: pageNumber },
    layout: createQXDocumentLayout({
      pages: [{ pageId: `irpage_${String(pageNumber)}`, pageNumber, width: opened.pageWidth, height: opened.pageHeight }],
    }),
    blocks,
    assets,
  });
}
